<template>
  <div class="attribute-dict-page">
    <!-- 左侧：属性分类树 -->
    <div class="left-panel content_block">
      <CategoryPanel ref="categoryPanel" @category-change="handleCategoryChange" @open-dictionary-info="handleOpenDictionaryInfo" />
    </div>

    <!-- 右侧：属性字典释义 -->
    <div v-if="showDictionaryInfo" class="right-panel content_block dictionary-info-panel">
      <div class="dict-info-title">属性字典释义：</div>
      <div class="dict-info-desc">
        在软件系统中，属性字典（Data Dictionary）是用于统一定义、描述和管理系统内所有数据元素的权威元数据集合。它以结构化的方式，将每个数据项的名称、类型、格式、取值范围、业务含义及关联关系进行标准化编码，确保不同模块、团队与系统间对数据的理解保持一致，请一定要重视属性字典的维护。
      </div>
      <div class="dict-info-rules">
        <p>1、系统内置属性字典由软件厂商维护，用户可以引用，也可以禁用，但不可以修改。</p>
        <p>2、用户自定义属性字典由用户自定义不可与系统内置属性字典名称重复，用户可以根据自身需求进行增删改。</p>
      </div>
    </div>

    <!-- 右侧：属性列表 -->
    <div v-else class="right-panel content_block" :class="{ 'is-fullscreen': isFullScreen }">
      <!-- 分类基本信息 -->
      <div v-if="currentCategory && !isFullScreen" class="category-info-block">
        <div class="info-block-title">基本信息</div>
        <div class="info-fields">
          <div class="info-item">
            <span class="info-label">分类名称</span>
            <span class="info-value">{{ currentCategory.name || '-' }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">来源</span>
            <span class="info-value">{{ currentCategory.source === 'PLATFORM' ? '平台分类' : '我的分类' }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">分类状态</span>
            <span class="info-value" style="display:flex;align-items:center;gap:8px;">
              <span>{{ currentCategory.enableFlagDesc || '-' }}</span>
              <el-button
                v-if="currentCategory.enableFlag ? $withPermission('attributeDict_category_disable') : $withPermission('attributeDict_category_enable')"
                type="text"
                size="mini"
                @click="handleCategoryToggle"
              >{{ currentCategory.enableFlag ? '禁用' : '启用' }}</el-button>
            </span>
          </div>
          <div class="info-item">
            <span class="info-label">创建人</span>
            <span class="info-value">{{ currentCategory.source === 'PLATFORM' ? '--' : (currentCategory.createUserName || '--') }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">创建时间</span>
            <span class="info-value">{{ currentCategory.createTime | date-format }}</span>
          </div>
        </div>
      </div>

      <!-- 工具栏 -->
      <div class="toolbar">
        <div class="toolbar-left">
          <el-tooltip
            v-if="currentCategory && categoryHasChildren"
            content="该分类存在子分类，不允许新增属性"
            placement="top"
          >
            <el-button
              type="primary"
              size="small"
              icon="el-icon-plus"
              disabled
            >新增属性字典</el-button>
          </el-tooltip>
          <el-button
            v-if="currentCategory && !categoryHasChildren && $withPermission('attributeDict_add')"
            type="primary"
            size="small"
            icon="el-icon-plus"
            @click="handleAdd"
          >新增属性字典</el-button>
        </div>
        <div class="toolbar-right">
          <el-button
            v-if="currentCategory"
            size="small"
            @click="handleOpenRecycleBin"
          >回收站</el-button>
          <el-input
            v-model="searchName"
            placeholder="请输入属性名称"
            size="small"
            clearable
            style="width:220px;"
            @clear="handleSearch"
            @keyup.enter.native="handleSearch"
          >
            <el-button slot="append" icon="el-icon-search" @click="handleSearch"></el-button>
          </el-input>
          <el-button class="tools-button" title="高级搜索" @click="toggleAdvancedSearch">
            <img :src="showAdvancedSearch ? require('@/assets/adSearch_checked.svg') : require('@/assets/adSearch.svg')" class="tools-icon" />
          </el-button>
          <el-button class="tools-button" :title="isFullScreen ? '退出全屏' : '全屏显示'" @click="toggleFullScreen">
            <img :src="isFullScreen ? require('@/assets/ic_zhankai.svg') : require('@/assets/ic_shouqi.svg')" class="tools-icon" />
          </el-button>
          <el-button class="tools-button" title="刷新" @click="handleSearch">
            <img src="@/assets/ic_refresh.svg" class="tools-icon" />
          </el-button>
        </div>
      </div>

      <!-- 属性表格 -->
      <el-table
        v-loading="loading"
        :data="list"
        border
        size="small"
        style="width:100%;"
      >
        <el-table-column label="属性名称" prop="name" min-width="150" show-overflow-tooltip></el-table-column>
        <el-table-column label="来源" width="90" align="center">
          <template slot-scope="{ row }">{{ row.source === 'PLATFORM' ? '平台' : '我的' }}</template>
        </el-table-column>
        <el-table-column label="属性值类型" prop="valueTypeDesc" width="120"></el-table-column>
        <el-table-column label="备注" prop="remark" min-width="180" show-overflow-tooltip></el-table-column>
        <el-table-column label="创建人" width="100">
          <template slot-scope="{ row }">{{ row.source === 'PLATFORM' ? '--' : row.createUserName }}</template>
        </el-table-column>
        <el-table-column width="150" :render-header="renderCreateTimeHeader">
          <template slot-scope="{ row }">{{ row.createTime | date-format }}</template>
        </el-table-column>
        <el-table-column label="状态" width="90" align="center">
          <template slot-scope="{ row }">
            <el-tag size="small" :type="row.enableFlag ? 'success' : 'info'" effect="plain">{{ row.enableFlagDesc }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="160" fixed="right" align="center">
          <template slot-scope="{ row }">
            <template v-if="row.source === 'PLATFORM'">
              <el-button type="text" size="mini" @click="handleView(row)">查看</el-button>
              <el-button v-if="row.enableFlag ? $withPermission('attributeDict_disable') : $withPermission('attributeDict_enable')" type="text" size="mini" @click="handlePlatformToggle(row)">{{ row.enableFlag ? '禁用' : '启用' }}</el-button>
            </template>
            <template v-else>
              <el-button type="text" size="mini" @click="handleView(row)">查看</el-button>
              <el-button v-if="$withPermission('attributeDict_update')" type="text" size="mini" @click="handleEdit(row)">编辑</el-button>
              <el-button v-if="row.enableFlag ? $withPermission('attributeDict_disable') : $withPermission('attributeDict_enable')" type="text" size="mini" @click="handleToggle(row)">{{ row.enableFlag ? '禁用' : '启用' }}</el-button>
              <el-button v-if="$withPermission('attributeDict_del')" type="text" size="mini" class="btn-danger" @click="handleDelete(row)">删除</el-button>
            </template>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination-bar">
        <el-pagination
          background
          layout="total, sizes, prev, pager, next"
          :total="total"
          :page-sizes="[10, 20, 50]"
          :page-size.sync="pageSize"
          :current-page.sync="pageNum"
          @size-change="handleSearch"
          @current-change="loadData"
        ></el-pagination>
      </div>

      <!-- 无分类选中提示 -->
      <div v-if="!currentCategory" class="empty-tip">
        <i class="el-icon-arrow-left"></i> 请从左侧选择属性分类
      </div>
    </div>

    <!-- 高级搜索抽屉 -->
    <el-drawer
      title="高级搜索"
      :visible.sync="showAdvancedSearch"
      direction="rtl"
      size="360px"
      :append-to-body="true"
    >
      <div class="advanced-search-body">
        <el-form :model="advancedForm" label-width="100px" size="small">
          <el-form-item label="属性名称">
            <el-input v-model="advancedForm.name" placeholder="请输入属性名称" clearable></el-input>
          </el-form-item>
          <el-form-item label="状态">
            <el-select v-model="advancedForm.enableFlag" placeholder="全部" clearable style="width:100%;">
              <el-option label="已启用" :value="true"></el-option>
              <el-option label="已禁用" :value="false"></el-option>
            </el-select>
          </el-form-item>
          <el-form-item label="属性值类型">
            <el-select v-model="advancedForm.valueType" placeholder="全部" clearable style="width:100%;">
              <el-option label="单选" value="SINGLE"></el-option>
              <el-option label="多选" value="MULTI"></el-option>
              <el-option label="自定义输入" value="CUSTOM"></el-option>
            </el-select>
          </el-form-item>
        </el-form>
        <div class="advanced-search-footer">
          <el-button size="small" @click="handleAdvancedReset">重置</el-button>
          <el-button type="primary" size="small" @click="handleAdvancedSearch">查询</el-button>
        </div>
      </div>
    </el-drawer>

    <EditAttributeDialog ref="editAttributeDialog" @success="handleSearch" />
    <RecycleBinDialog ref="recycleBinDialog" @restored="handleSearch" />
  </div>
</template>

<script>
import CategoryPanel from './components/CategoryPanel.vue';
import EditAttributeDialog from './components/EditAttributeDialog.vue';
import RecycleBinDialog from './components/RecycleBinDialog.vue';
import {
  getAttributePageList,
  deleteAttribute,
  enableOrDisableAttribute,
  platformEnableOrDisableAttribute,
  enableOrDisableAttributeCategory,
  platformEnableOrDisableAttributeCategory,
} from '@/api/attributeDict.js';

export default {
  name: 'AttributeDict',
  components: { CategoryPanel, EditAttributeDialog, RecycleBinDialog },
  computed: {
    categoryHasChildren () {
      return this.currentCategory && this.currentCategory.children && this.currentCategory.children.length > 0;
    },
  },
  data () {
    return {
      showDictionaryInfo: false,
      showAdvancedSearch: false,
      isFullScreen: false,
      advancedForm: { name: '', enableFlag: null, valueType: null },
      currentCategory: null,
      searchName: '',
      createTimeSort: null,
      loading: false,
      list: [],
      total: 0,
      pageNum: 1,
      pageSize: 10,
    };
  },
  methods: {
    toggleAdvancedSearch () {
      this.showAdvancedSearch = !this.showAdvancedSearch;
    },
    toggleFullScreen () {
      this.isFullScreen = !this.isFullScreen;
    },
    handleAdvancedSearch () {
      this.showAdvancedSearch = false;
      this.handleSearch();
    },
    handleAdvancedReset () {
      this.advancedForm = { name: '', enableFlag: null, valueType: null };
    },
    handleOpenDictionaryInfo () {
      this.showDictionaryInfo = true;
    },
    handleCategoryChange (category) {
      this.showDictionaryInfo = false;
      this.currentCategory = category;
      this.searchName = '';
      if (category) {
        this.handleSearch();
      } else {
        this.list = [];
        this.total = 0;
      }
    },
    handleSearch () {
      this.pageNum = 1;
      this.loadData();
    },
    renderCreateTimeHeader (h) {
      return h('span', {
        style: { cursor: 'pointer', userSelect: 'none', display: 'inline-flex', alignItems: 'center' },
        on: { click: this.handleSortCreateTime },
      }, [
        '创建时间',
        h('span', { style: { display: 'inline-flex', flexDirection: 'column', marginLeft: '4px' } }, [
          h('i', { class: 'el-icon-caret-top', style: { fontSize: '12px', lineHeight: '7px', color: this.createTimeSort === 'ASC' ? '#409EFF' : '#C0C4CC' } }),
          h('i', { class: 'el-icon-caret-bottom', style: { fontSize: '12px', lineHeight: '7px', color: this.createTimeSort === 'DESC' ? '#409EFF' : '#C0C4CC' } }),
        ]),
      ]);
    },
    handleSortCreateTime () {
      if (this.createTimeSort === null) {
        this.createTimeSort = 'ASC';
      } else if (this.createTimeSort === 'ASC') {
        this.createTimeSort = 'DESC';
      } else {
        this.createTimeSort = null;
      }
      this.handleSearch();
    },
    loadData () {
      if (!this.currentCategory) return;
      this.loading = true;
      const params = {
        source: this.currentCategory.source,
        name: this.advancedForm.name || this.searchName || null,
        enableFlag: this.advancedForm.enableFlag != null ? this.advancedForm.enableFlag : undefined,
        valueType: this.advancedForm.valueType || undefined,
        pageNum: this.pageNum,
        pageSize: this.pageSize,
        orderBy: this.createTimeSort ? [{ orderByProperty: 'createTime', orderByType: this.createTimeSort }] : undefined,
      };
      if (this.currentCategory.source === 'PLATFORM') {
        params.attributeCategoryId = this.currentCategory.id;
      } else {
        params.attributeCategoryTenantId = this.currentCategory.id;
      }
      getAttributePageList(params).then(res => {
        if (res.heads.code === 200) {
          this.list = res.body?.list || [];
          this.total = res.body?.total || 0;
        }
      }).finally(() => { this.loading = false; });
    },
    handleCategoryToggle () {
      const category = this.currentCategory;
      const action = category.enableFlag ? '禁用' : '启用';
      const isPlatform = category.source === 'PLATFORM';
      const msg = isPlatform
        ? `确定要${action}平台分类「${category.name}」吗？该操作仅影响本企业。`
        : `确定要${action}分类「${category.name}」吗？`;
      this.$confirm(msg, `${action}分类`, {
        confirmButtonText: action,
        cancelButtonText: '取消',
        type: 'warning',
      }).then(() => {
        const api = isPlatform ? platformEnableOrDisableAttributeCategory : enableOrDisableAttributeCategory;
        const params = isPlatform
          ? { attributeCategoryId: category.id, enableFlag: !category.enableFlag }
          : { id: category.id, enableFlag: !category.enableFlag };
        api(params).then(res => {
          if (res.heads.code === 200) {
            this.$message.success(`${action}成功`);
            this.$refs.categoryPanel.loadTree();
            this.currentCategory = { ...category, enableFlag: !category.enableFlag, enableFlagDesc: category.enableFlag ? '已禁用' : '已启用' };
          }
        });
      }).catch(() => {});
    },
    handleAdd () {
      if (this.currentCategory.source === 'PLATFORM') {
        this.$refs.editAttributeDialog.open({ categoryPlatformId: this.currentCategory.id });
      } else {
        this.$refs.editAttributeDialog.open({ categoryTenantId: this.currentCategory.id });
      }
    },
    handleView (row) {
      this.$refs.editAttributeDialog.open({ id: row.id, viewMode: true, source: row.source });
    },
    handleEdit (row) {
      this.$refs.editAttributeDialog.open({ id: row.id, categoryTenantId: this.currentCategory ? this.currentCategory.id : null });
    },
    handleToggle (row) {
      const action = row.enableFlag ? '禁用' : '启用';
      this.$confirm(`确定要${action}属性「${row.name}」吗？`, action, { type: 'warning' }).then(() => {
        enableOrDisableAttribute({ id: row.id, enableFlag: !row.enableFlag }).then(res => {
          if (res.heads.code === 200) {
            this.$message.success(`${action}成功`);
            this.loadData();
          }
        });
      }).catch(() => {});
    },
    handlePlatformToggle (row) {
      const action = row.enableFlag ? '禁用' : '启用';
      this.$confirm(`确定要${action}平台属性「${row.name}」吗？该操作仅影响本企业。`, action, { type: 'warning' }).then(() => {
        platformEnableOrDisableAttribute({ attributeId: row.id, enableFlag: !row.enableFlag }).then(res => {
          if (res.heads.code === 200) {
            this.$message.success(`${action}成功`);
            this.loadData();
          }
        });
      }).catch(() => {});
    },
    handleDelete (row) {
      this.$confirm(`确定要删除属性「${row.name}」吗？删除后可在回收站还原。`, '删除属性', {
        confirmButtonText: '删除',
        cancelButtonText: '取消',
        type: 'warning',
      }).then(() => {
        deleteAttribute({ id: row.id }).then(res => {
          if (res.heads.code === 200) {
            this.$message.success('删除成功');
            this.handleSearch();
          }
        });
      }).catch(() => {});
    },
    handleOpenRecycleBin () {
      const category = this.currentCategory;
      if (!category) return;
      if (category.source === 'PLATFORM') {
        this.$refs.recycleBinDialog.open({ categoryPlatformId: category.id });
      } else {
        this.$refs.recycleBinDialog.open({ categoryTenantId: category.id });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.attribute-dict-page {
  display: flex;
  height: 100%;
  gap: 10px;
}

.left-panel {
  width: 280px;
  flex-shrink: 0;
  overflow: hidden;
  padding: 0;
}

.right-panel {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  padding: 12px 16px;
  position: relative;
}

.category-info-block {
  margin-bottom: 12px;
  padding: 12px 16px;
  background: #F8F9FC;
  border-radius: 4px;
  border: 1px solid #EBEEF5;
  flex-shrink: 0;
}

.info-block-title {
  font-size: 13px;
  font-weight: 600;
  color: #303133;
  padding-left: 8px;
  border-left: 3px solid #409EFF;
  margin-bottom: 10px;
}

.info-fields {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 0;
}

.info-item {
  width: 25%;
  min-width: 160px;
  display: flex;
  align-items: center;
  font-size: 13px;
}

.info-label {
  color: #606266;
  white-space: nowrap;
  &::after { content: '：'; }
}

.info-value {
  color: #1F2733;
  font-weight: 500;
}

.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
  flex-shrink: 0;
}

.toolbar-left {
  display: flex;
  gap: 8px;
}

.toolbar-right {
  display: flex;
  align-items: center;
  gap: 8px;

  .el-button + .el-button {
    margin-left: 0;
  }
}

.tools-button {
  width: 32px;
  height: 32px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  .tools-icon {
    width: 16px;
    height: 16px;
  }
}

.pagination-bar {
  display: flex;
  justify-content: flex-end;
  margin-top: 12px;
  flex-shrink: 0;
}

.empty-tip {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: #C0C4CC;
  font-size: 14px;
}

.right-panel.is-fullscreen {
  position: fixed !important;
  left: 0 !important;
  right: 0 !important;
  top: 0 !important;
  bottom: 0 !important;
  z-index: 9999 !important;
  background: #fff !important;
  border-radius: 0 !important;
  margin: 0 !important;
}

.advanced-search-body {
  padding: 16px;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.advanced-search-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 16px;
}

.dictionary-info-panel {
  padding: 20px 24px;
  overflow-y: auto;
}

.dict-info-title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 12px;
}

.dict-info-desc {
  font-size: 13px;
  color: #606266;
  line-height: 1.8;
  margin-bottom: 12px;
}

.dict-info-rules {
  font-size: 13px;
  color: #606266;
  line-height: 1.8;

  p { margin: 4px 0; }
}

.btn-danger {
  color: #F56C6C !important;
  &:hover { color: #f78989 !important; }
}

/deep/ .el-table {
  flex: 1;
}
</style>
